import { ChangeDetectorRef } from '@angular/core';
import { IAllSelectable } from './interfaces/all-selectable.interface';

export class SelectionContext {
	/** Tableau des indices des éléments selectionnés. */
	public selections: number[] = [];

	/** Tableau des indices des éléments selectionnés via la touche SHIFT. */
	public currentSelectionSpan: number[] = [];

	/** Index du dernier élément sélectionné. */
	public lastSingleSelection: number;

	/** Empêche le traitement du 2eme clic lorsque que l'utilisateur effectue un double clic. */
	private preventClic = false;

	/**
	 * Constructeur.
	 * @param cdRef : Detecteur de changement.
	 */
	constructor(
		private cdRef: ChangeDetectorRef,
		private souceComponent: IAllSelectable
	) {}

	/**
	 * Appelé lorsque l'utilisateur clique sur un élément de la liste.
	 * @param event : Evenment clic de la souris.
	 * @param index : Index de l'élément cliqué.
	 */
	onMouseClick(event: MouseEvent, index: number) {
		setTimeout(() => {
			// Réactive le traitement d'un autre clic au bout de 200ms.
			this.preventClic = false;
		}, 200);

		if (this.preventClic === true) {
			// Clic survenu moins de 200ms après le precedent --> ignoré.
			return;
		}

		// Empeche le traitement d'un autre clic pendant les prochaines 200ms.
		this.preventClic = true;

		// Détermine s'il s'agit d'une sélection multiple par SHIFT (l'élément sélectionné doit être différent de l'élement sélectionné precedemment
		// et la touche SHIFT doit être appuyé)
		this.handleSelection(event, index);
	}

	/**
	 * Traite les événements clavier de type KeyDown sur le document.
	 * @param event Événement du clavier.
	 */
	public handleKeyboardEvent(event: KeyboardEvent) {
		if (
			event.key === 'a' &&
			(event.ctrlKey || event.metaKey) &&
			this.selections.length &&
			document.activeElement.nodeName !== 'INPUT'
		) {
			// Si la combinaison de touches CTRL + A ou Command + a (Mac) sont interceptées sur un element HTML autre que INPUT...
			event.preventDefault(); // Ignore le comportement par défaut du navigateur.
			this.selectAll(); // Selectionne tous les elements.
			event.stopPropagation();
		} else if (event.key === 'Escape') {
			// Si la touche ECHAP est pressée
			this.clearSelection();
			event.stopPropagation();
		}
	}

	/**
	 * Gère la sélection.
	 * @param event : Événement clic de la souris.
	 * @param index : Index de la ligne cliquée.
	 */
	public handleSelection(event: MouseEvent, index: number) {
		const shiftSelect =
			event.shiftKey &&
			(this.lastSingleSelection || this.lastSingleSelection === 0) &&
			this.lastSingleSelection !== index;

		if (!this.selections || this.selections.length < 1) {
			// Si la selection est vide, initialise le mode selection.
			this.selections = [index]; // Ajoute l'element cliqué à la selection.
			this.lastSingleSelection = index; // Garde une trace du dernier élément cliqué.
		} else if (event.metaKey || event.ctrlKey) {
			// Si la touche CTRL ou Command (Mac) est pressée...
			let selectedIndex = this.selections.findIndex((x) => x === index); // Verifie si l'élément cliqué est déja sélectionné

			if (selectedIndex != -1) {
				// Si l'element est déja selectionné, le retire de la selection.
				this.selections.splice(selectedIndex, 1);
				this.lastSingleSelection = null; // Vide la trace du dernier élément cliqué.
			} else {
				this.selections.push(index); // Ajoute l'element cliqué à la selection.
				this.lastSingleSelection = index; // Garde une trace du dernier élément cliqué.
			}
		} else if (shiftSelect) {
			// Si la touche SHIFT est pressée...
			const newSelectionBefore = index < this.lastSingleSelection; // Vérifie si l'élement cliqué se situe avant le precedent élement cliqué
			const count = newSelectionBefore
				? this.lastSingleSelection - (index - 1)
				: index + 1 - this.lastSingleSelection; // Determine le nombre d'élements situés entre l'élement cliqué et le precedent élement cliqué

			// Vide la selection precedente
			if (
				this.currentSelectionSpan &&
				this.currentSelectionSpan.length > 0
			) {
				this.currentSelectionSpan.forEach((x) => {
					let index = this.selections.indexOf(x);
					if (index != -1) {
						this.selections.splice(index, 1);
					}
				});
				this.currentSelectionSpan = [];
			}

			// Construit la selection lié au SHIFT en stockant les index situés entre l'élement cliqué et le precedent élement cliqué
			for (let i = 0; i < count; i++) {
				if (newSelectionBefore) {
					this.currentSelectionSpan.push(
						this.lastSingleSelection - i
					);
				} else {
					this.currentSelectionSpan.push(
						this.lastSingleSelection + i
					);
				}
			}

			let tempSelection: number[] = [...this.selections];

			// Ajoute chaque élement de la selection SHIFT à la selection globale
			this.currentSelectionSpan.forEach((x) => {
				let index = tempSelection.indexOf(x);
				if (index === -1) {
					tempSelection.push(x);
				}
			});

			this.selections = tempSelection;
		} else {
			// Cas d'une selection unitaire.
			let selectedIndex = this.selections.findIndex((x) => x === index); // Verifie si l'élément cliqué est déja sélectionné

			if (
				(selectedIndex === -1 && !event.shiftKey) ||
				(selectedIndex !== -1 && this.selections.length > 1)
			) {
				// Si l'éleemnt n'est pas selectionné
				// ou si l'element est déja selectionné mais qu'il ne s'agit pas du seul élément selectionné.
				// alors la selection est vidée et l'élement est ajouté à la selection.
				this.clearSelection();
				this.selections = [index];
				this.lastSingleSelection = index; // Garde une trace du dernier élément cliqué.
			} else if (selectedIndex !== -1) {
				// Si l'element est déja selectionné et qu'il s'agit du seul élément selectionné vide la selection.
				this.clearSelection();
			}
		}

		if (!event.shiftKey) {
			// Si la touche SHIFT n'est pas pressée, vide la selection SHIFT.
			this.currentSelectionSpan = [];
		}

		//this.selectionChanged.emit(this.selections.map(i => this.items[i]));
		this.cdRef.detectChanges();
	}

	/**
	 * Vide la selection.
	 */
	clearSelection() {
		if (this.selections.length) {
			this.selections = []; // Vide la selection globale
			this.currentSelectionSpan = []; // Vide la selection lié au SHIFT
			this.lastSingleSelection = null; // Vide la trace du dernier élément cliqué.
			//this.selectionChanged.emit(this.selections.map(i => this.items[i]));
			this.cdRef.detectChanges();
		}
	}

	/**
	 * Sélectionne tous les éléments de la liste.
	 */
	selectAll() {
		const allIds = this.souceComponent.getAllIds();

		if (this.selections.length !== allIds.length) {
			// Si tous les élements ne sont pas déja selectionnés
			this.selections = allIds; // Ajoute tous les elements à la selection
			this.currentSelectionSpan = []; // Vide la selection lié au SHIFT
			this.lastSingleSelection = null; // Vide la trace du dernier élément cliqué.
			//this.selectionChanged.emit(this.items);
			this.cdRef.detectChanges();
		}
	}

	/**
	 * Indique si l'élément specifié est selectionné.
	 * @param index : index de l'élément à tester
	 * @returns True si l'élément est selectionné, ou false autrement.
	 */
	isSelected(index: number): boolean {
		return this.selections.indexOf(index) >= 0;
	}
}
